import { AfterViewInit, ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {FormGroup, FormControl, Validators} from '@angular/forms';
import {
  System,
  ProductModelService,
  InverterV2,
  isNotNullOrUndefined,
} from 'ngx-core-services';;
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { differenceInCalendarDays, startOfDay } from 'date-fns';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { SystemsState, requestTelemetryReport, selectedSystem } from '../../store';

interface ResumeContent {
  /**
   * date: yyyy-MM-dd
   */
  [date: string]: {
    [serial: string]: Partial<InverterV2>[];
  }
}

const connextLabel = 'CONNEXT';
const azzurroHubLabel = 'AZZURRO HUB';

export enum TelemetryReportMode {
  FiveMinuteTelemetry = 'fiveMinuteTelemetry',
  DailyAggregate = 'dailyAggregate',
}

export const exportReportMaxDaysForMode = {
  [TelemetryReportMode.FiveMinuteTelemetry]: 7,
  [TelemetryReportMode.DailyAggregate]: 90,
};

const supportedDeviceTypes = [
  "connext" as const,
  "inverter" as const,
  "meter" as const,
  "enerclic" as const,
  "azzurro-hub" as const,
];

const labels = {
  aggregateContent: 'Download up to ninety days of aggregated annual energy data from your photovoltaic system with ease. This new feature allows you to access and analyze the overall performance of your installation, helping you optimize consumption and monitor the health of your system. Simply select the desired time range and download your data.',
  daily: 'Daily',
  endDate: 'End Date',
  everyFiveMinutes: 'Every five minutes',
  exportReport: 'Export Report',
  fiveMinuteContent: 'Download up to seven days of telemetry data from your photovoltaic system with ease. This feature allows you to access and analyze the performance of your solar installation, helping you optimize energy usage and monitor system health. Simply select the desired date range, and export your data in a convenient format.',
  startDate: 'Start Date',
  title: 'Telemetry Export',
};

const today = new Date();
const month = today.getMonth();
const year = today.getFullYear();
const day = today.getDate();
@CreateBlockReduxSupport()
@Component({
  selector: 'lib-telemetry-report',
  templateUrl: './telemetry-report.component.html',
  styleUrls: ['./telemetry-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TelemetryReportComponent implements AfterViewInit, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();
  @Input() date = {
    year,
    month,
    day,
  };

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return { ...this._labels };
  }

  selection = new FormGroup({
    start: new FormControl(new Date(year, month, day - 1), Validators.required),
    end: new FormControl(new Date(year, month, day), Validators.required),
  });

  today = today;
  TelemetryReportMode = TelemetryReportMode;
  exportReportMaxDaysForMode = exportReportMaxDaysForMode;

  readonly selectedSystem$ = this.store.select(selectedSystem).pipe(isNotNullOrUndefined());
  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();
  readonly selectedMode$ = new BehaviorSubject<TelemetryReportMode>(TelemetryReportMode.FiveMinuteTelemetry);
readonly deviceIds$ = this.selectedSystem$.pipe(isNotNullOrUndefined()).pipe(
  map((system) => {
    const currentDevices = (system.devices ?? []).map((device) => device.id);
    const uninstallations = (system.metadata?.verifiedUninstallations ?? []).map((device) => device.sn);

    let allDevices = (currentDevices.concat(uninstallations)).filter((deviceId) => {
      const product = new ProductModelService(deviceId);
      const productType = product.type?.name as any;
      return supportedDeviceTypes.includes(productType);
    }).map((deviceId) => {
      const product = new ProductModelService(deviceId);
      if (product.type?.name === 'connext') return connextLabel;
      if (product.type?.name === 'azzurro-hub') return azzurroHubLabel;
      return deviceId;
    });

    const hasConnext = allDevices.includes(connextLabel);
    const hasAzzurroHub = allDevices.includes(azzurroHubLabel);

    // --- Add EVCH devices ---
    if (hasAzzurroHub) {
      const evch485Sn = ((system.data as any)?.evCharger485 || []).map((ev: any) => ev.sn);
      const evchCANSn = ((system.data as any)?.evChargerCAN || []).map((ev: any) => ev.sn);
      const heatpumpSn = ((system.data as any)?.heatPump || []).map((hp: any) => hp.sn);
      allDevices = [...allDevices, ...evch485Sn, ...evchCANSn, ...heatpumpSn];
    }

    if (hasConnext) {
      const evchDevices = ((system.data as any)?.devices || [])
        .filter((device: any) => {
          const product = new ProductModelService(device.sn);
          return product.type?.name === 'evcharge';
        })
        .map((device: any) => device.sn);
      allDevices = [...allDevices, ...evchDevices];
    }

    return allDevices;
  }),
  shareReplay({ bufferSize: 1, refCount: true }),
);

  constructor(
    readonly store: Store<SystemsState>
  ) { }

  ngAfterViewInit(): void {
    if (this.date.day !== day || this.date.month !== month || this.date.year !== year) {
      this.selection.setValue({
        start: new Date(this.date.year, this.date.month, this.date.day - 1),
        end: new Date(this.date.year, this.date.month, this.date.day),
      });
    }
  }

  reportRequest(system: System, deviceIds: string[], instanceId: string) {
    const start = this.selection.value.start;
    const end = this.selection.value.end;

    deviceIds = deviceIds.map((deviceId) => {
      const product = new ProductModelService(deviceId);
      if ( product.type?.name === 'inverter' || product.type?.name === 'enerclic' ) {
        return deviceId.toUpperCase();
      }

      if (deviceId === connextLabel) {
        deviceId = system.configuration.connextId ? system.configuration.connextId.toUpperCase() : '';
      }
      if (deviceId === azzurroHubLabel) {
        deviceId = system.configuration.azzurroHubId ? system.configuration.azzurroHubId.toUpperCase() : '';
      }

      return deviceId;
    });

    if( !start || !end ) {
      return
    }

    const mode = this.selectedMode$.value;
    this.store.dispatch(requestTelemetryReport({ systemId: system.uid!, instanceId, deviceIds, start, end, mode}));
  }

  changeSelectMode(mode: TelemetryReportMode) {
    this.selectedMode$.next(mode);

    const maxRange = exportReportMaxDaysForMode[mode];
    const { start, end } = this.selection.value;

    if (start && end) {
      const startDate = startOfDay(start);
      const endDate = startOfDay(end);
      const daysSelected = differenceInCalendarDays(endDate, startDate) + 1;

      if (daysSelected > maxRange) {
        this.selection.setValue({ start: null, end: null });
      }
    }
  }
}
